import type { ICatalog, ISavedView, ISavedViewWrite, ITag, ITagWrite, ITreeNode, IUserRef, IWorkspace, IWorkspaceMember } from '@platform/core/types';
import { ApiError } from './ApiError';
import type { HttpClient } from './HttpClient';
import type { IActivityCount } from './IActivityCount';
import type { IActivityCountQuery } from './IActivityCountQuery';
import type { IActivityList } from './IActivityList';
import type { IActivityQuery } from './IActivityQuery';
import type { IActivityWireItem } from './IActivityWireItem';
import type { IInvitationList } from './IInvitationList';
import type { ILinkTypeUsage } from './ILinkTypeUsage';
import type { IRolePermissions } from './IRolePermissions';
import type { ITreeDeleteResult } from './ITreeDeleteResult';
import type { ITreeNodeWrite } from './ITreeNodeWrite';
import type { IWorkspaceModuleWrite } from './IWorkspaceModuleWrite';
import type { IWorkspaceWrite } from './IWorkspaceWrite';
import type { PathKind } from './PathKind';
import { toQuery } from './toQuery';
import type { IInvitationWrite } from './IInvitationWrite';
import type { IWorkspaceDirectory } from './IWorkspaceDirectory';
import type { IWorkspaceInvitation } from './IWorkspaceInvitation';

export class WorkspaceDao {
  constructor(private readonly http: HttpClient) {}

  mine(): Promise<IWorkspace[]> { return this.http.get('/workspaces'); }
  /** Каталог усіх робочих просторів платформи (GET /workspaces/directory); сервер без цього API (404 / 405) — { available: false }. */
  async directory(): Promise<IWorkspaceDirectory> {
    try {
      return { available: true, items: await this.http.get('/workspaces/directory') };
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 405)) return { available: false, items: [] };
      throw e;
    }
  }
  get(id: string): Promise<IWorkspace> { return this.http.get(`/workspaces/${id}`); }
  /** Новий робочий простір (прототип v4 «Створити робочий простір»): обов'язкові модулі — завжди, додаткові — з переліку. */
  create(body: IWorkspaceWrite): Promise<IWorkspace> { return this.http.post('/workspaces', body); }
  members(id: string): Promise<IWorkspaceMember[]> { return this.http.get(`/workspaces/${id}/members`); }
  /** Зміна ролі учасника (PATCH /members/{userId} { role }): власника змінює лише власник, єдиного власника — не можна (403 / 409). */
  changeMemberRole(id: string, userId: string, role: string): Promise<IWorkspaceMember> { return this.http.patch(`/workspaces/${id}/members/${userId}`, { role }); }
  /** Вилучення учасника з простору (DELETE /members/{userId}); єдиного власника вилучити не можна (409). */
  removeMember(id: string, userId: string): Promise<void> { return this.http.delete(`/workspaces/${id}/members/${userId}`); }
  searchUsers(id: string, q: string): Promise<IUserRef[]> { return this.http.get(`/workspaces/${id}/users`, { q }); }
  tags(id: string): Promise<ITag[]> { return this.http.get(`/workspaces/${id}/tags`); }
  createTag(id: string, body: ITagWrite): Promise<ITag> { return this.http.post(`/workspaces/${id}/tags`, body); }
  deleteTag(id: string, tagId: string): Promise<void> { return this.http.delete(`/workspaces/${id}/tags/${tagId}`); }
  /** Новий вузол шляхів, зміна (назва, колір напряму, дати ітерації) і видалення гілки (записи переходять до батьківського вузла). */
  createNode(id: string, kind: PathKind, body: ITreeNodeWrite): Promise<ITreeNode> { return this.http.post(`/workspaces/${id}/${kind}`, body); }
  updateNode(id: string, kind: PathKind, nodeId: string, body: ITreeNodeWrite): Promise<ITreeNode> { return this.http.patch(`/workspaces/${id}/${kind}/${nodeId}`, body); }
  deleteNode(id: string, kind: PathKind, nodeId: string): Promise<ITreeDeleteResult> { return this.http.delete(`/workspaces/${id}/${kind}/${nodeId}`); }
  /** «Ролі й дозволи»: матриця, зміна однієї клітинки (лише власник) і повернення до типової. */
  rolePermissions(id: string): Promise<IRolePermissions> { return this.http.get(`/workspaces/${id}/role-permissions`); }
  setRolePermission(id: string, role: string, capability: string, allowed: boolean): Promise<IRolePermissions> {
    return this.http.put(`/workspaces/${id}/role-permissions/${encodeURIComponent(role)}/${encodeURIComponent(capability)}`, { allowed });
  }
  resetRolePermissions(id: string): Promise<IRolePermissions> { return this.http.delete(`/workspaces/${id}/role-permissions`); }
  /** Увімкнення й перейменування модуля в просторі; відповідь — оновлений каталог простору. */
  updateModule(id: string, moduleKey: string, body: IWorkspaceModuleWrite): Promise<ICatalog> { return this.http.patch(`/workspaces/${id}/modules/${encodeURIComponent(moduleKey)}`, body); }
  /**
   * Скинути модуль простору (рішення 2 етапу 9; одна дія сервера з аудитом): без типу — назва й доступність типові, перевизначення всіх типів
   * модуля скидаються; з типом — лише перевизначення цього типу (головного — ще й назва й доступність). Додані поля зберігаються.
   */
  async resetModule(id: string, moduleKey: string, recordType?: string): Promise<void> {
    await this.http.post<unknown>(`/workspaces/${id}/modules/${encodeURIComponent(moduleKey)}/reset${toQuery({ recordType })}`);
  }
  /** Скільки кінців зв'язків кожного типу є в просторі («Використовується» сторінки «Типи зв’язків»). */
  linkTypeUsage(id: string): Promise<ILinkTypeUsage[]> { return this.http.get(`/workspaces/${id}/link-types/usage`); }
  /** Налаштування простору поверх налаштувань платформи (fieldLayout — лише змінені параметри); скидання ключа повертає глобальні значення. */
  settings(id: string): Promise<Record<string, unknown>> { return this.http.get(`/workspaces/${id}/settings`); }
  setSetting(id: string, key: string, value: unknown): Promise<Record<string, unknown>> { return this.http.put(`/workspaces/${id}/settings/${encodeURIComponent(key)}`, value); }
  resetSetting(id: string, key: string): Promise<Record<string, unknown>> { return this.http.delete(`/workspaces/${id}/settings/${encodeURIComponent(key)}`); }
  areas(id: string): Promise<ITreeNode[]> { return this.http.get(`/workspaces/${id}/areas`); }
  iterations(id: string): Promise<ITreeNode[]> { return this.http.get(`/workspaces/${id}/iterations`); }
  views(id: string, recordType?: string): Promise<ISavedView[]> { return this.http.get(`/workspaces/${id}/views`, { recordType }); }
  saveView(id: string, body: ISavedViewWrite, viewId?: string): Promise<ISavedView> {
    return viewId ? this.http.put(`/workspaces/${id}/views/${viewId}`, body) : this.http.post(`/workspaces/${id}/views`, body);
  }
  deleteView(id: string, viewId: string): Promise<void> { return this.http.delete(`/workspaces/${id}/views/${viewId}`); }

  /** Запрошення простору; сервер без цього API (404 / 405) — { available: false }, а не помилка сторінки. */
  async invitations(id: string): Promise<IInvitationList> {
    try {
      return { available: true, items: await this.http.get<IWorkspaceInvitation[]>(`/workspaces/${id}/invitations`) };
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 405)) return { available: false, items: [] };
      throw e;
    }
  }
  invite(id: string, body: IInvitationWrite): Promise<IWorkspaceInvitation> { return this.http.post(`/workspaces/${id}/invitations`, body); }
  /** Попросити доступ до чужого простору (POST /access-requests); рішення приймає власник чи адміністратор простору. */
  requestAccess(id: string, note?: string): Promise<unknown> { return this.http.post(`/workspaces/${id}/access-requests`, note ? { note } : {}); }

  /**
   * Стрічка активності простору (GET /activity?limit=&before=): сторінки підряд курсором before (ідентифікатор останньої події), доки є
   * повні сторінки, не вичерпано `pages` і події не давніші за `since`. Сервер без цього API (404 / 405) — { available: false }.
   */
  async activity(q: IActivityQuery): Promise<IActivityList> {
    const items: IActivityWireItem[] = [];
    let before: number | undefined;
    for (let page = 0; page < (q.pages ?? 1); page += 1) {
      let chunk: IActivityWireItem[];
      try {
        chunk = await this.http.get<IActivityWireItem[]>(`/workspaces/${q.ws}/activity`, { limit: q.limit, before, actor: q.actor, recordType: q.recordType, q: q.q });
      } catch (e) {
        if (e instanceof ApiError && (e.status === 404 || e.status === 405)) return { available: false, items: [], more: false };
        throw e;
      }
      items.push(...chunk);
      const last = chunk[chunk.length - 1];
      const done = chunk.length < q.limit || !last || (!!q.since && Date.parse(last.at) < Date.parse(q.since));
      if (done) return { available: true, items, more: !!last && chunk.length >= q.limit };
      before = last.id;
    }
    return { available: true, items, more: true };
  }
  /** Кількість подій стрічки за `days` днів (0 — усі події простору) з фільтрами стрічки: «Зміни · 7 днів», «Показано 150 з N» журналу аудиту. */
  activityCount(c: IActivityCountQuery): Promise<IActivityCount> {
    return this.http.get(`/workspaces/${c.ws}/activity/count`, { days: String(c.days), actor: c.actor, recordType: c.recordType, q: c.q });
  }
  revokeInvitation(id: string, invitationId: string): Promise<void> { return this.http.delete(`/workspaces/${id}/invitations/${invitationId}`); }
  /** Надіслати запрошення повторно (POST …/resend): сервер оновлює час надсилання й термін дії. */
  resendInvitation(id: string, invitationId: string): Promise<IWorkspaceInvitation> { return this.http.post(`/workspaces/${id}/invitations/${invitationId}/resend`, {}); }
}
